/**
 * 「這一格是機翻還是人寫的」——記號的計算規則。
 *
 * 抽成純函式是因為它的判斷比看起來細：同一次儲存裡，有的格子要標成機翻待校、有的
 * 要**解除**標記，而解除的條件是「人改過了」。寫在元件裡會混在表單狀態的雜訊中，
 * 也沒辦法單獨驗。
 *
 * 規則只有兩條：
 *   1. 這次採用的譯文，若送出時的內容**仍等於**當初填進去的譯文 → `mt_draft`
 *      （人只是按了套用，沒有實際校對）
 *   2. 伺服器上原本標著機翻的格子，若這次送出的內容**與伺服器上的不同** → 解除
 *      （人動手改了，就算校對過）
 *
 * 沒被這兩條命中的格子完全不出現在 patch 裡——後端是逐格合併，沒提到的就維持原狀。
 */

import type { DisplayFormState, DisplayLocale } from './SpotDisplayFields';
import type { Locale, SpotFormState } from './spot-form';

/** 可機翻的目標語系；繁中是主稿，不會有機翻記號。 */
export const MT_LOCALES = ['en', 'ja'] as const satisfies readonly DisplayLocale[];
export type MtLocale = (typeof MT_LOCALES)[number];

/** 這次套用的譯文：`欄位_i18n|語系` → 當初填進表單的文字。 */
export type MtDraftMap = Record<string, string>;

export type TranslationStatePatch = Record<string, Record<string, 'mt_draft' | null>>;

/** `poi_title_i18n` → `poi_title`（窗屏欄位在表單狀態裡用不帶字尾的鍵）。 */
export function formKeyOf(field: string): string {
  return field.replace(/_i18n$/, '');
}

/**
 * 從表單狀態讀出某一格目前的值。
 *
 * 表單把內容分在三個地方：名稱與描述在頂層（那是給網站與購票機看的），窗屏六欄在
 * `display` 底下。翻譯與記號計算都要能一視同仁地問「這一格現在是什麼」，所以這個
 * 對照只寫一次——散在各處遲早會有人漏掉 name/description，症狀是記號永遠清不掉。
 */
export function currentValue(form: SpotFormState, field: string, locale: string): string {
  if (field === 'name_i18n') return form.name[locale as Locale] ?? '';
  if (field === 'description_i18n') return form.description[locale as Locale] ?? '';
  const display = form.display as DisplayFormState;
  return display[formKeyOf(field) as keyof DisplayFormState]?.[locale as DisplayLocale] ?? '';
}

export function draftKey(field: string, locale: string): string {
  return `${field}|${locale}`;
}

/**
 * 算出這次儲存要送的翻譯記號增減；沒有任何變化時回 undefined（不送這個欄位）。
 *
 * @param form         目前的表單狀態（名稱、描述與窗屏六欄）
 * @param drafts       這次套用過的譯文（尚未儲存）
 * @param serverState  伺服器上目前的記號
 * @param serverValues 伺服器上目前的文字值，用來判斷人有沒有改過
 */
export function buildTranslationStatePatch(input: {
  form: SpotFormState;
  drafts: MtDraftMap;
  serverState: Record<string, Record<string, string>>;
  serverValues: Record<string, Record<string, string> | null | undefined>;
}): TranslationStatePatch | undefined {
  const patch: TranslationStatePatch = {};
  const mark = (field: string, locale: string, value: 'mt_draft' | null): void => {
    patch[field] = { ...(patch[field] ?? {}), [locale]: value };
  };

  const current = (field: string, locale: string): string =>
    currentValue(input.form, field, locale);

  // 規則 1：這次採用的譯文，沒被改過就是待校
  for (const [key, draftText] of Object.entries(input.drafts)) {
    const [field, locale] = key.split('|') as [string, string];
    mark(field, locale, current(field, locale) === draftText ? 'mt_draft' : null);
  }

  // 規則 2：伺服器上標著機翻的格子，內容被改過就解除
  for (const [field, locales] of Object.entries(input.serverState)) {
    for (const locale of Object.keys(locales)) {
      if (patch[field]?.[locale] !== undefined) continue; // 規則 1 已經處理過
      const serverValue = input.serverValues[field]?.[locale] ?? '';
      if (current(field, locale) !== serverValue) mark(field, locale, null);
    }
  }

  return Object.keys(patch).length === 0 ? undefined : patch;
}

/** 把所有機翻記號一次清掉（「全部標記已校對」）。 */
export function clearAllPatch(
  serverState: Record<string, Record<string, string>>,
): TranslationStatePatch | undefined {
  const patch: TranslationStatePatch = {};
  for (const [field, locales] of Object.entries(serverState)) {
    for (const locale of Object.keys(locales)) {
      patch[field] = { ...(patch[field] ?? {}), [locale]: null };
    }
  }
  return Object.keys(patch).length === 0 ? undefined : patch;
}

/** 機翻待校的格數，給總覽的提示列用。 */
export function countMtDrafts(serverState: Record<string, Record<string, string>>): number {
  return Object.values(serverState).reduce((n, locales) => n + Object.keys(locales).length, 0);
}
